import * as React from "react"
import { SvgCheckCircle } from "@digits-shared/components/SVGIcons/line/CheckCircle.svg"
import { SvgCopy02 } from "@digits-shared/components/SVGIcons/line/Copy02.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import {
  HoverPopover,
  HoverPopoverContent,
  type HoverPopoverProps,
  HoverPopoverTrigger,
} from "@digits-shared/DesignSystem/Popovers/HoverPopover"
import { DetailText, LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"

const StyledHoverPopoverContent = styled(HoverPopoverContent)`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 360px;
`

interface CopyableTextHoverProps extends HoverPopoverProps {
  label?: string
  text: string | undefined | null
  disabled?: boolean
}

export const CopyableTextHoverPopover: React.FC<
  React.PropsWithChildren<CopyableTextHoverProps>
> = ({ label, text, disabled, children, ...rest }) => {
  const [hasCopied, setHasCopied] = React.useState(false)
  const copyTextToClipboard = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      await navigator.clipboard.writeText(text ?? "")
      setHasCopied(true)
    },
    [text]
  )

  React.useEffect(() => {
    let timer: NodeJS.Timeout | undefined
    if (hasCopied) {
      timer = setTimeout(() => {
        setHasCopied(false)
      }, 3000)
    }

    return () => clearTimeout(timer)
  }, [hasCopied])

  if (!text || disabled) return children

  return (
    <HoverPopover>
      <HoverPopoverTrigger asChild style={{ cursor: "pointer" }}>
        {children}
      </HoverPopoverTrigger>

      <StyledHoverPopoverContent {...rest}>
        <div>
          {label && <LabelText weight="heavy">{label}</LabelText>}
          <DetailText>{text}</DetailText>
        </div>

        <DigitsButton
          size="medium"
          $circle
          $variant={hasCopied ? "primary-outline" : "secondary-dark"}
          onClick={copyTextToClipboard}
        >
          {hasCopied ? <SvgCheckCircle /> : <SvgCopy02 />}
        </DigitsButton>
      </StyledHoverPopoverContent>
    </HoverPopover>
  )
}
